import { Form, type SelectProps } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import type { ProjectInvoice } from "../../../../../types/project-invoices.types.tsx"
import { UNRESOLVED_USER_LABEL } from "../../../../../../../utils/select/entity-labels.ts"
import { ResolvedSelect } from "../../../../../../../components/Select/ResolvedSelect.tsx"

type Group = "assignee" | "customer"

type AssigneeOption = {
  label: string
  value: string | number
  group: Group
}

interface ProjectViewInvoiceAssigneeSelectProps
  extends Omit<SelectProps, "options" | "value" | "onChange"> {
  form: FormInstance<ProjectInvoice>
  options?: {
    subcontractors?: { label: string; value: string | number }[]
    customers?: { label: string; value: string | number }[]
  }
}

export function ProjectViewInvoiceAssigneeSelect({
  form,
  options,
  placeholder,
  className,
  ...props
}: ProjectViewInvoiceAssigneeSelectProps) {
  const { t } = useTranslation()

  const assignee =
    Form.useWatch("assignee", form) || form?.getFieldValue("assignee")
  const customer =
    Form.useWatch("customer", form) || form?.getFieldValue("customer")

  const groupedOptions = useMemo(() => {
    const list: { label: string; options: AssigneeOption[] }[] = []

    if (options?.subcontractors?.length) {
      list.push({
        label: t("invoice.assignee.assignees"),
        options: options.subcontractors.map((s) => ({
          label: s.label || t("invoice.assignee.noName"),
          value: s.value,
          group: "assignee"
        }))
      })
    }

    if (options?.customers?.length) {
      list.push({
        label: t("invoice.assignee.customers"),
        options: options.customers.map((c) => ({
          label: c.label || t("invoice.assignee.noName"),
          value: c.value,
          group: "customer"
        }))
      })
    }

    return list
  }, [options, t])

  const value = assignee ?? customer ?? undefined

  const handleChange: SelectProps["onChange"] = (val, option) => {
    if (!val) {
      form.setFieldsValue({ assignee: null, customer: null })
      return
    }

    const { group } = option as AssigneeOption

    form.setFieldsValue({
      assignee: group === "assignee" ? val : null,
      customer: group === "customer" ? val : null
    })
  }

  return (
    <>
      <Form.Item<ProjectInvoice> noStyle>
        <ResolvedSelect
          showSearch
          allowClear
          optionFilterProp="label"
          placeholder={placeholder ?? t("invoice.assignee.placeholder")}
          options={groupedOptions}
          fallbackLabel={UNRESOLVED_USER_LABEL}
          value={value}
          onChange={handleChange}
          className={className ?? "w-full"}
          {...props}
        />
      </Form.Item>

      <Form.Item<ProjectInvoice> name="assignee" hidden>
        <select />
      </Form.Item>
      <Form.Item<ProjectInvoice> name="customer" hidden>
        <select />
      </Form.Item>
    </>
  )
}
